import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import {
  useChangeSubscriptionPlan,
  useScheduledPlanChange,
  useCancelScheduledPlanChange,
  usePlanOptions,
  useGetCheckoutSession,
  useOrganization,
  useSubscriptions,
} from "@/client/backend/organization";
import type { components } from "@/client/backend/zz_schema";
import { handleApiError } from "@/utils/api";
import { AnalyticsEvents, captureAnalyticsEvent } from "@/analytics";

type PlanOption = components["schemas"]["PlanOption"];
const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
const date = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export function BillingPlans({
  onContact,
  onManage,
}: {
  onContact: () => void;
  onManage: () => void;
}) {
  const [organization] = useOrganization();
  const orgId = organization === "loading" ? "loading" : organization.id;
  const {
    options,
    isLoading: plansLoading,
    error: plansError,
    mutate: refreshPlans,
  } = usePlanOptions(orgId);
  const { data: response } = useSubscriptions(orgId);
  const { trigger: checkout } = useGetCheckoutSession(orgId);
  const { trigger: changePlan } = useChangeSubscriptionPlan(orgId);
  const { data: scheduleResponse, error: scheduleError, mutate: refreshSchedule } = useScheduledPlanChange(orgId);
  const { trigger: cancelChange } = useCancelScheduledPlanChange(orgId);
  const scheduledChange = scheduleResponse?.data?.change;
  const { toast } = useToast();
  const [interval, setInterval] = useState<"Monthly" | "Yearly">("Monthly");
  const [busy, setBusy] = useState(false);
  const active = response?.data?.filter((s) => s.status === "Active") ?? [];
  const current = active.find((s) => s.planType !== "OnDemand") ?? active[0];

  // Both actions end on a Stripe page: hosted Checkout for a first purchase, the
  // portal confirmation for a change. Stripe applies upgrades now and schedules
  // downgrades for renewal, so the console never quotes or classifies a change.
  const choose = async (option: PlanOption) => {
    if (busy || orgId === "loading") return;
    if (option.action !== "subscribe" && option.action !== "change") return;
    const p = option.plan;
    setBusy(true);
    try {
      if (option.action === "subscribe") {
        captureAnalyticsEvent(AnalyticsEvents.BILLING_CHECKOUT_STARTED, {
          plan_type: p.planType === "OnDemand" ? "on_demand" : p.planType === "Yearly" ? "yearly" : "monthly",
          subscription_count: "0",
        });
      }
      const result =
        option.action === "subscribe"
          ? await checkout({ priceId: p.stripePriceId })
          : await changePlan({ priceId: p.stripePriceId });
      if (result?.data?.url) window.location.assign(result.data.url);
      else handleApiError(result, toast);
    } catch (error) {
      toast({
        title: "Could not open Stripe",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const keepCurrentPlan = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await cancelChange();
      if (!handleApiError(result, toast)) return;
      await Promise.all([refreshSchedule(), refreshPlans()]);
      toast({ description: "Scheduled change removed. Your current plan will continue." });
    } catch {
      toast({ title: "Could not remove the scheduled change", description: "Please refresh and try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Plans" className="space-y-4">
      {active.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div className="space-y-2">
              <CardTitle>{current?.planName ?? "Your plan"}</CardTitle>
              <CardDescription>
                {current?.planType === "Yearly"
                  ? "Billed yearly. Credits renew monthly."
                  : "Manage your subscription, payment method, and scheduled changes."}
              </CardDescription>
            </div>
            <Button variant="outline" onClick={onManage}>
              Manage in Stripe
            </Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {current?.monthlyMinutes && (
              <p>
                {current.monthlyMinutes.toLocaleString()} credits included each
                month.
              </p>
            )}
            {current?.nextCreditRefillAt && (
              <p>Next credit renewal: {date(current.nextCreditRefillAt)}.</p>
            )}
            {current?.cancelAt && (
              <p>Your plan ends {date(current.cancelAt)}.</p>
            )}
            {scheduledChange && (
              <div className="space-y-2 rounded-md border p-3">
                <p>Switching to {scheduledChange.plan.name}, billed {scheduledChange.plan.planType === "Yearly" ? "yearly" : "monthly"}, on {date(scheduledChange.effectiveAt)}.</p>
                <Button variant="outline" disabled={busy} onClick={() => void keepCurrentPlan()}>
                  Keep current plan
                </Button>
              </div>
            )}
            {(scheduleError || scheduleResponse?.error) && (
              <p role="alert" className="text-muted-foreground">
                {scheduleResponse?.error?.message ?? "Could not load scheduled changes. Please refresh to try again."}
              </p>
            )}
            <p className="text-muted-foreground">
              {active.some((s) => s.planType === "OnDemand")
                ? "Overage billing is enabled. You can manage it in Stripe."
                : "Overage billing is disabled."}
            </p>
          </CardContent>
        </Card>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Choose your plan</h2>
        <div
          className="flex gap-1 rounded-lg border bg-background p-1"
          aria-label="Billing interval"
        >
          {(["Monthly", "Yearly"] as const).map((choice) => (
            <Button
              key={choice}
              size="sm"
              variant={interval === choice ? "default" : "ghost"}
              aria-pressed={interval === choice}
              onClick={() => setInterval(choice)}
            >
              {choice}
            </Button>
          ))}
        </div>
      </div>
      {plansError && (
        <p role="alert" className="text-sm text-destructive">
          Could not load plans. Please refresh the page to try again.
        </p>
      )}
      {plansLoading && (
        <p className="text-sm text-muted-foreground">Loading plans…</p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {options
          .filter((option) => option.plan.planType === interval || option.plan.planType === "OnDemand")
          .map((option) => {
            const p = option.plan;
            const metered = p.planType === "OnDemand";
            const overageRate = option.overageRate ?? p.minutePrice;
            const price = metered ? 0 :
              interval === "Yearly" ? p.yearlyPrice : p.monthlyPrice;
            const credits = p.monthlyMinutes;
            return (
              <Card key={p.stripePriceId}>
                <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
                  <CardTitle>{p.name}</CardTitle>
                  {option.custom && <Badge variant="secondary">Custom</Badge>}
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    <span className="text-3xl font-semibold">
                      {price == null
                        ? "Unavailable"
                        : money(interval === "Yearly" ? price / 12 : price)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {" "}
                      / month
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {metered ? "No monthly subscription fee" : interval === "Yearly" && price != null
                      ? `${money(price)} billed yearly`
                      : "Billed monthly"}
                  </p>
                  <p className="font-medium">
                    {metered ? "Use your remaining free credits first" : `${credits?.toLocaleString()} credits each month`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {metered
                      ? overageRate == null ? "Overage pricing is unavailable." : `Then ${money(overageRate)} per credit, billed monthly. Enabling overage does not add or renew free credits.`
                      : option.overageRate != null
                      ? `Unused credits expire each month. Overage is ${money(option.overageRate)} per credit, enabled by default with an opt-out in checkout.`
                      : "Unused credits expire each month."}
                  </p>
                  <Button
                    className="w-full"
                    disabled={
                      busy ||
                      option.action === "current" ||
                      option.action === "unavailable" ||
                      price == null ||
                      (metered && overageRate == null) ||
                      (!metered && credits == null)
                    }
                    onClick={() => void choose(option)}
                  >
                    {busy
                      ? "Please wait"
                      : {
                          subscribe: metered ? "Enable overage" : `Choose ${p.name}`,
                          change: `Switch to ${p.name}`,
                          current: "Current plan",
                          unavailable: "Unavailable",
                        }[option.action]}
                  </Button>
                  {option.reason && (
                    <p className="text-sm text-muted-foreground">
                      {option.reason}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
      </div>
      <Card>
        <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-semibold">Need more than Individual?</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Contact us for Scale, Powerhouse, and custom plans.
            </p>
          </div>
          <Button variant="outline" onClick={onContact}>
            Contact us
          </Button>
        </CardContent>
      </Card>
    </section>
  );
}
